import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SKILLS, REGIONS } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Calendar, Award, Users, FileText, Pencil, Check, Loader2 } from "lucide-react";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(user);

  if (!user) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSkill = (s) =>
    setForm((f) => ({ ...f, skills: (f.skills || []).includes(s) ? f.skills.filter((x) => x !== s) : [...(f.skills || []), s] }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/members/me", {
        full_name: form.full_name, date_of_birth: form.date_of_birth,
        place_of_birth: form.place_of_birth, residence: form.residence, region: form.region,
        skills: form.skills, other_skills: form.other_skills,
        committee_interests: form.committee_interests, desired_committees: form.desired_committees,
      });
      setUser(data);
      setEditing(false);
      toast.success("Membership dossier updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-2">Member Dossier</div>
            <h1 className="font-serif-display text-5xl font-medium tracking-tight text-[#F5F3EF]">{user.full_name}</h1>
            <p className="font-mono-doc text-sm text-[#A8A29E] mt-2">Member No. {user.member_id}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/certificate" data-testid="dash-certificate-link">
              <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A]">
                <FileText className="w-4 h-4 mr-2" /> View Certificate
              </Button>
            </Link>
            <Dialog open={editing} onOpenChange={(o) => { setEditing(o); if (o) setForm(user); }}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white" data-testid="dash-edit-btn">
                  <Pencil className="w-4 h-4 mr-2" /> Edit Profile
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto bg-[#141414] border-[#D4AF37]/30 text-[#F5F3EF]">
                <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#F5F3EF]">Edit Membership Dossier</DialogTitle></DialogHeader>
                <div className="space-y-5 py-2">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Full Name</Label><Input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="edit-fullname" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Date of Birth</Label><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="edit-dob" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Location of Birth</Label><Input value={form.place_of_birth || ""} onChange={(e) => set("place_of_birth", e.target.value)} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="edit-pob" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Residence</Label><Input value={form.residence || ""} onChange={(e) => set("residence", e.target.value)} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="edit-residence" /></div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Region</Label>
                      <Select value={form.region || ""} onValueChange={(v) => set("region", v)}>
                        <SelectTrigger className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="edit-region"><SelectValue placeholder="Select region" /></SelectTrigger>
                        <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Contributory Skills</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {SKILLS.map((s) => {
                        const active = (form.skills || []).includes(s);
                        return (
                          <button type="button" key={s} onClick={() => toggleSkill(s)}
                            className={`flex items-center gap-2 border px-3 py-2 text-left text-xs ${active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F5F3EF]" : "border-[#F5F3EF]/15 text-[#D6D2CA]"}`}>
                            <span className={`w-4 h-4 border flex items-center justify-center ${active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#F5F3EF]/30"}`}>{active && <Check className="w-2.5 h-2.5 text-[#0A0A0A]" />}</span>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Other Skills</Label><Textarea value={form.other_skills || ""} onChange={(e) => set("other_skills", e.target.value)} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Desired New Committees / Programs</Label><Textarea value={form.desired_committees || ""} onChange={(e) => set("desired_committees", e.target.value)} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="edit-desired" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={save} disabled={saving} className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white" data-testid="edit-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 bg-[#141414] border border-[#D4AF37]/30 p-8 hover:border-[#D4AF37] transition-colors">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-6">Personal Record</div>
            <div className="space-y-5">
              <Row icon={Calendar} label="Date of Birth" value={user.date_of_birth || "—"} />
              <Row icon={MapPin} label="Location of Birth" value={user.place_of_birth || "—"} />
              <Row icon={MapPin} label="Current Residence" value={user.residence || "—"} />
              <Row icon={MapPin} label="Region" value={user.region || "—"} />
            </div>
          </div>

          <div className="md:col-span-7 bg-black border border-[#D4AF37]/30 text-white p-8">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-6 flex items-center gap-2"><Award className="w-4 h-4" /> Contributory Skills</div>
            <div className="flex flex-wrap gap-2">
              {(user.skills || []).length ? user.skills.map((s) => (
                <span key={s} className="border border-[#D4AF37]/40 text-[#F5F3EF] text-sm px-3 py-1.5">{s}</span>
              )) : <p className="text-white/50 text-sm">No skills recorded yet.</p>}
            </div>
            {user.other_skills && <p className="mt-6 text-sm text-white/70 leading-relaxed border-t border-white/10 pt-4">{user.other_skills}</p>}
          </div>

          <div className="md:col-span-7 bg-[#141414] border border-[#D4AF37]/30 p-8">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-6 flex items-center gap-2"><Users className="w-4 h-4" /> Committee Interests</div>
            <div className="flex flex-wrap gap-2">
              {(user.committee_interests || []).length ? user.committee_interests.map((s) => (
                <span key={s} className="border border-[#5DBB7A]/40 bg-[#5DBB7A]/10 text-[#5DBB7A] text-sm px-3 py-1.5">{s}</span>
              )) : <p className="text-[#A8A29E] text-sm">No committee interests yet.</p>}
            </div>
            {user.desired_committees && (
              <div className="mt-6 border-t border-[#F5F3EF]/10 pt-4">
                <div className="text-xs uppercase tracking-wider text-[#A8A29E] mb-1">Proposed New Committee / Program</div>
                <p className="text-sm leading-relaxed text-[#D6D2CA]">{user.desired_committees}</p>
              </div>
            )}
            <Link to="/committees" className="inline-block mt-6">
              <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] text-sm">Browse & Join Committees</Button>
            </Link>
          </div>

          <div className="md:col-span-5 bg-[#141414] border border-[#D4AF37]/30 p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-3">Status of Membership</div>
              <div className="flex items-center gap-2 text-[#5DBB7A] font-medium"><Check className="w-5 h-5" /> Declaration Signed</div>
              <p className="text-sm text-[#A8A29E] mt-3 italic font-serif-display text-lg text-[#D4AF37]">"{user.signature_name || user.full_name}"</p>
              <p className="text-xs text-[#A8A29E] mt-1">Digital Signature on Record</p>
            </div>
            <Link to="/certificate" className="mt-6">
              <Button className="w-full rounded-none bg-[#C8102E] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="dash-view-cert-btn">View Membership Certificate</Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-[#D4AF37] mt-0.5" strokeWidth={1.5} />
      <div>
        <div className="text-xs uppercase tracking-wider text-[#A8A29E]">{label}</div>
        <div className="text-base font-medium text-[#F5F3EF]">{value}</div>
      </div>
    </div>
  );
}
